import { autorun } from "mobx";
import { CountryWithStateModel } from "./CountryModels";
import { TimeZoneModel } from "./TimeZoneModel";
import {
  AuthService,
  CoreInputModelUtil,
  CurrencyTypes,
  DateFormat,
  getNewErrors,
  InputErrorModel,
  Languages,
  NumberFormat,
  PhoneFormatTypes,
} from "@mindmemobile/mm-shared-global";
import { CategoriesModel } from "./CategoriesModel";
import { IAPIGeneralSettingsModel } from "../interfaces"; 
import TimeZoneStore from "../store/TimeZoneStore";

export class GeneralSettingsModel {
  country: CountryWithStateModel;
  countries: CountryWithStateModel[];
  timeZone: TimeZoneModel;
  dateFormat: keyof typeof DateFormat;
  numberFormat: keyof typeof NumberFormat;
  currency: keyof typeof CurrencyTypes;
  phoneFormat: keyof typeof PhoneFormatTypes;
  language: string;
  isUseDayLightSavings: boolean;
  includeCountry: boolean;
  countryId: string;
  timeZoneId: string;
  categories: CategoriesModel[];

  errors: InputErrorModel<GeneralSettingsModel>[];

  constructor(data?: Partial<GeneralSettingsModel>) {
    this.country = data?.country
      ? new CountryWithStateModel(data?.country)
      : new CountryWithStateModel();
    this.countries =
      data?.countries?.map((country) => new CountryWithStateModel(country)) ||
      [];
    this.timeZone = data?.timeZone || new TimeZoneModel();
    this.dateFormat =
      data?.dateFormat || ("Format1" as keyof typeof DateFormat);
    this.numberFormat =
      data?.numberFormat || ("Format1" as keyof typeof NumberFormat);
    this.currency = data?.currency || CurrencyTypes.USD;
    this.phoneFormat =
      data?.phoneFormat || ("Format1" as keyof typeof PhoneFormatTypes);
    this.language = data?.language || Languages.English;
    this.isUseDayLightSavings = data?.isUseDayLightSavings || false;
    this.countryId = data?.countryId || "";
    this.timeZoneId = data?.timeZoneId || "";
    this.includeCountry = data?.includeCountry || false;
    this.categories = (data?.categories || []).map(
      (category) => new CategoriesModel(category)
    );

    this.errors = data?.errors || [];

    autorun(() => {
      this.validate(true);
    });
  }
  getTimeZones(): TimeZoneModel[] {
    return TimeZoneStore.timeZones.filter(
      (tz) =>
        tz.countryId === this.country.id ||
        tz.alternateCountryIds.includes(this.country.id)
    );
  }

  filterTimeZones() {
    const filteredTimeZones = TimeZoneStore.timeZones.filter(
      (tz) =>
        tz.countryId === this.country.id ||
        tz.alternateCountryIds.includes(this.country.id)
    );
    if (filteredTimeZones.length > 0) {
      this.timeZone = filteredTimeZones[0];
    } else {
      this.timeZone = new TimeZoneModel();
    }
  }

  sortCountriesByName = () => {
    return this.countries.slice().sort((a, b) => a.name.localeCompare(b.name));
  };

  hasError(key: keyof GeneralSettingsModel): boolean {
    return this.errors.find((f) => f.field == key) !== undefined;
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<GeneralSettingsModel>[] = [];

    CoreInputModelUtil.validateTextField(
      this.country.name,
      "country",
      newErrors
    );
    CoreInputModelUtil.validateTextField(
      this.timeZone.id,
      "timeZone",
      newErrors
    );
    CoreInputModelUtil.validateTextField(
      this.dateFormat,
      "dateFormat",
      newErrors
    );
    CoreInputModelUtil.validateTextField(
      this.numberFormat,
      "numberFormat",
      newErrors
    );
    CoreInputModelUtil.validateTextField(this.currency, "currency", newErrors);
    CoreInputModelUtil.validateTextField(
      this.phoneFormat,
      "phoneFormat",
      newErrors
    );
    CoreInputModelUtil.validateTextField(this.language, "language", newErrors);

    if (isAutoRun) {
      this.errors = getNewErrors(newErrors, this.errors);
    } else {
      this.errors = newErrors;
    }
    return this.errors.length === 0;
  }

  createApiModel(): IAPIGeneralSettingsModel {
    return {
      accountId: AuthService.authData.accountId,
      countryId: this.country.id,
      timeZoneId: this.timeZone.id,
      dateFormat: this.dateFormat,
      numberFormat: this.numberFormat,
      currency: this.currency,
      phoneFormat: this.phoneFormat,
      preferredLanguage: this.language,
      isUseDayLightSavings: this.isUseDayLightSavings,
      includeCountry: this.includeCountry,
      categories: this.categories.map((category) => category.createAPIModel()),
    };
  }

  static deserialize(data: IAPIGeneralSettingsModel): GeneralSettingsModel {
    const mappedData: Partial<GeneralSettingsModel> = {
      countryId: data.countryId && data.countryId,
      timeZoneId: data.timeZoneId && data.timeZoneId,
      dateFormat: data.dateFormat,
      numberFormat: data.numberFormat as keyof typeof NumberFormat,
      currency: data.currency,
      phoneFormat: data.phoneFormat,
      language: data.preferredLanguage,
      isUseDayLightSavings: data.isUseDayLightSavings,
      includeCountry: data.includeCountry,
      categories:
        data.categories?.map((category) =>
          CategoriesModel.deserialize(category)
        ) || [],
    };

    return new GeneralSettingsModel(mappedData);
  }
}
